// NextGenBuilder/hooks/useNextGenBack.js
//
// One Back behaviour for every NextGen Builder screen.
//
// Before this, "Back" meant two different things depending on which arrow you
// happened to hit. NextGenLayout's header arrow called
// navigate('/nextgen-builder/dashboard') unconditionally, while
// EliteTechnologyMastery's own header arrow ran a chain of internal-state
// checks and only navigated once every one of them was already null. Two
// controls, same icon, same place on screen, different outcomes — which is
// why Back appeared to "not work" intermittently.
//
// Why not just navigate(-1) everywhere:
//   window.history.length counts the WHOLE browsing session, including pages
//   from other sites, so it is >1 even when the user opened this route in a
//   fresh tab. Going -1 there walks the user out of the application.
//   location.key is the reliable signal: React Router assigns 'default' to
//   the very first entry it sees, so key !== 'default' means the router
//   itself pushed at least one entry and there is genuinely somewhere
//   in-app to go back to.

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export const NEXTGEN_DASHBOARD_PATH = '/nextgen-builder/dashboard';

/**
 * @param {string} fallbackPath where to land when there is no in-app history
 * @returns {() => void} a stable handler safe to pass straight to onClick
 */
export const useNextGenBack = (fallbackPath = NEXTGEN_DASHBOARD_PATH) => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    const hasInAppHistory = location.key && location.key !== 'default';
    if (hasInAppHistory && typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    // Direct load, hard refresh, or a tab opened straight onto this route:
    // replace rather than push so Back does not bounce between the two.
    navigate(fallbackPath, { replace: true });
  }, [navigate, location.key, fallbackPath]);
};

export default useNextGenBack;
